import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { AlertCircle, XCircle, Clock, Users, Home } from 'lucide-react';

type ErrorType = 'not_found' | 'revoked' | 'expired' | 'limit_reached' | 'already_cloned' | 'generic';

interface ShareErrorCardProps {
  errorType: ErrorType;
  onGoHome: () => void;
}

const errorConfig: Record<ErrorType, {
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  description: string;
}> = {
  not_found: {
    icon: XCircle,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-100',
    get title() { return translateInterfaceText("Link não encontrado"); },
    get description() { return translateInterfaceText("Este link de compartilhamento não existe ou foi removido."); },
  },
  revoked: {
    icon: AlertCircle,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-100',
    get title() { return translateInterfaceText("Compartilhamento desativado"); },
    get description() { return translateInterfaceText("O produtor desativou este compartilhamento. Entre em contato com ele para solicitar um novo link."); },
  },
  expired: {
    icon: Clock,
    iconColor: 'text-slate-500',
    iconBg: 'bg-slate-100',
    get title() { return translateInterfaceText("Link expirado"); },
    get description() { return translateInterfaceText("Este link expirou e não está mais disponível. Solicite um novo link ao produtor."); },
  },
  limit_reached: {
    icon: Users,
    iconColor: 'text-slate-500',
    iconBg: 'bg-slate-100',
    get title() { return translateInterfaceText("Limite de cópias atingido"); },
    get description() { return translateInterfaceText("Este link atingiu o limite máximo de cópias permitido. Entre em contato com o produtor."); },
  },
  already_cloned: {
    icon: AlertCircle,
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-100',
    get title() { return translateInterfaceText("Vídeo já clonado"); },
    get description() { return translateInterfaceText("Você já clonou este vídeo. Cada vídeo pode ser copiado apenas uma vez para sua conta."); },
  },
  generic: {
    icon: AlertCircle,
    iconColor: 'text-red-500',
    iconBg: 'bg-red-100',
    get title() { return translateInterfaceText("Este compartilhamento não está disponível"); },
    get description() { return translateInterfaceText("Não foi possível processar este link. Tente novamente ou entre em contato com o produtor."); },
  },
};

export function ShareErrorCard({ errorType, onGoHome }: ShareErrorCardProps) {
  useLanguage();
  const config = errorConfig[errorType];
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="w-full bg-white rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.08)] overflow-hidden"
    >
      <div className="p-8 sm:p-10 text-center">
        {/* Error Icon */}
        <div className={`w-16 h-16 rounded-2xl ${config.iconBg} flex items-center justify-center mx-auto mb-5`}>
          <Icon className={`w-8 h-8 ${config.iconColor}`} />
        </div>

        {/* Title */}
        <h1 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
          {config.title}
        </h1>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
          {config.description}
        </p>
      </div>

      {/* CTA */}
      <div className="px-8 sm:px-10 pb-8 sm:pb-10">
        <Button
          onClick={onGoHome}
          className="w-full h-12 bg-[#4B7BFF] hover:bg-[#3B6BEF] text-white font-medium"
        >
          <Home className="w-4 h-4 mr-2" />
          {translateInterfaceText("Voltar para HostVSL")}</Button>
      </div>
    </motion.div>
  );
}
